import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { GridRowId } from '@mui/x-data-grid';
import CommonDataGrid from '../Stateless/CommonDataGrid';
import ErrorHandler from '../Stateless/Common/ErrorHandler';
import {
  useBlockUnblockItemCategory,
  useDeleteItemCategory,
  useGetItemCategories,
} from '../Query/ItemCategoryQuery';
import { useSetAtom } from 'jotai';
import { categoryStyles } from '../Styles/CategoryFormStyles';
import { useDebounce } from './Utils/useDounce';
import { useManageCmsParams } from './ManageCMS/useManageCmsParams';
import PermissionActionButtons from '../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../context/PermissionContext';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { itemSubCategoryAtom } from '../HelperFunctions/atoms';

interface ItemCategory {
  id: string;
  nameEnglish: string;
  nameArabic: string;
  status: 'blocked' | 'unblocked' | string;
  addedBy: string;
  [key: string]: any;
}

interface ItemCategoryListProps {
  editCategory: (_category: ItemCategory) => void;
  mode?: 'add' | 'edit';
  modal?: boolean;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
  setMode: React.Dispatch<React.SetStateAction<'add' | 'edit'>>;
  storeId?: string;
  addCategory: () => void;
}

const ItemCategoryList: React.FC<ItemCategoryListProps> = ({
  editCategory,
  setModal,
  setMode,
  addCategory,
}) => {
  const setitemSubCategory = useSetAtom(itemSubCategoryAtom);
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage] = useState<string>('');
  const [snackbarSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('info');

  const { params, dispatch } = useManageCmsParams();
  const { data: getItemCategory, isLoading, refetch } = useGetItemCategories(params);
  console.log('Component Render - getItemCategory:', getItemCategory);
  console.log('Component Render - Current Page:', params.page);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { mutate: deleteItemCategoryById } = useDeleteItemCategory();
  const { mutate: blockUnblockItemCategoryById } =
    useBlockUnblockItemCategory();

  useEffect(() => {
    console.log("Updated Params:", params);
  }, [params]);

  const filteredRows = useMemo(() => {
    if (!getItemCategory?.data) return [];
    const rows = (getItemCategory?.data || []).map((row: any, index: number) => ({
      ...row,
      id: row.id,
      serialNumber: index + 1 + (Number(params.page - 1)) * Number(params.limit),
      nameEnglish: row.nameEnglish || '',
      nameArabic: row.nameArabic || '',
      status: row.status || 'unblocked',
      addedBy: row.addedBy || '',
      storeId: row.storeId || '',
      storeType: row.storeType || '',
      merchantName: row.merchantName || '',
      storeName: row.storeName || '',
    }));
    const term = (params.search || '').toLowerCase();
    if (!term) return rows;
    return rows.filter((r: any) => {
      const nameEn = String(r.nameEnglish || '').toLowerCase();
      const nameAr = String(r.nameArabic || '').toLowerCase();
      const storeName = String(r.storeName || '').toLowerCase();
      return nameEn.includes(term) || nameAr.includes(term) || storeName.includes(term);
    });
  }, [params.search, getItemCategory, params.page, params.limit])

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch, dispatch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (newPage: number) => {
    const oneBasedPage = newPage + 1;
    if (params.page !== oneBasedPage) {
      dispatch({ type: 'SET_PAGE', payload: oneBasedPage });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    if (params.limit !== newSize) {
      dispatch({ type: 'SET_LIMIT', payload: newSize });
    }
  };

  const handleEditCategory = (category: ItemCategory) => {
    editCategory(category);
    setMode('edit');
    setModal(true);
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedCategoryIds.length === 0) {
      showMessage('Select at least one category to delete', 'error');

    } else {
      const confirmed = window.confirm(
        'Are you sure you want to delete the selected categories?'
      );
      if (!confirmed) return;

      deleteItemCategoryById(selectedCategoryIds, {
        onSuccess: () => {
          showMessage('Categories deleted successfully', 'success');
          setSelectedCategoryIds([]);
        },
        onError: () => showMessage('Error deleting categories', 'error'),
      });
    }
  }, [selectedCategoryIds, deleteItemCategoryById, showMessage]);

  const handleDelete = useCallback(
    (id: string) => {
      const confirmed = window.confirm(
        'Are you sure you want to delete this category?'
      );
      if (!confirmed) return;

      deleteItemCategoryById([id], {
        onSuccess: () => {
          showMessage('Category deleted successfully', 'success');
        },
        onError: () => showMessage('Error deleting category', 'error'),
      });
    },
    [deleteItemCategoryById, showMessage]
  );

  const handleBlock = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No category selected to block', 'error');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to block?');
    if (!confirmed) return;

    blockUnblockItemCategoryById(
      {
        id: selectedCategoryIds,
        data: { status: 'blocked' },
      },
      {
        onSuccess: () => {
          showMessage('Categories blocked successfully', 'success');
          setSelectedCategoryIds([]);
        },
        onError: () => {
          showMessage('Error blocking categories', 'error');
        }
      }
    );
  };

  const handleUnblock = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No category selected to unblock', 'error');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to unblock?');
    if (!confirmed) return;

    blockUnblockItemCategoryById(
      {
        id: selectedCategoryIds,
        data: { status: 'unblocked' },
      },
      {
        onSuccess: () => {
          showMessage('Categories unblocked successfully', 'success');
          setSelectedCategoryIds([]);
        },
        onError: () => {
          showMessage('Error unblocking categories', 'error');
        }
      }
    );
  };

  const { hasPermission } = usePermissions();

  // Determine which bulk buttons to show based on selected rows' statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedCategoryIds || selectedCategoryIds.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = getItemCategory?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.id), String(item.status || '')])
    );
    const statuses = selectedCategoryIds.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s !== 'unblocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return { showBlock: hasUnblocked, showUnblock: hasBlocked };
  }, [selectedCategoryIds, getItemCategory]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('itemCategory', 'Edit') || hasPermission('itemCategory', 'edit');
    const canDelete = hasPermission('itemCategory', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(row); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDelete(id); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns = [
    {
      field: 'serialNumber',
      headerName: 'S.No',
      width: 90,
      disableColumnMenu: true,
      sortable: true,
    },
    { field: 'nameEnglish', headerName: 'Category Name', flex: 1 },
    { field: 'storeName', headerName: 'Store Name', flex: 1 },
    { field: 'storeType', headerName: 'Store Type', flex: 1 },
    {
      field: 'manageSubCategory',
      headerName: 'Manage Sub Category',
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            console.log('params:', params.row);
            setitemSubCategory(params.row);
            navigate(`/manageItemSubCategory/${params.row.id}`);
          }}
          sx={{ textTransform: 'none' }}
        >
          Manage Sub Category
        </Button>
      ),
    },
    {
      field: 'addedBy',
      headerName: 'Added By',
      width: 120,
      disableColumnMenu: true,
    },

    ...(hasPermission('itemCategory', 'Block') || hasPermission('itemCategory', 'Unblock') ? [{
      field: 'status',
      headerName: 'Status',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => {
        const isBlocked = params.row.status !== 'unblocked';
        const action = isBlocked ? 'Unblock' : 'Block';

        // Check if user has permission for the specific action
        if (!hasPermission('itemCategory', action)) {
          return null;
        }

        const toggleStatus = async () => {
          const newStatus = isBlocked ? 'unblocked' : 'blocked';
          const confirmed = window.confirm(
            `Are you sure you want to ${newStatus} this Item Category?`
          );
          if (!confirmed) return;

          try {
            await blockUnblockItemCategoryById({
              id: [params.row.id],
              data: { status: newStatus },
            });

            showMessage(`Item Category ${newStatus} successfully`, 'success');
          } catch {
            showMessage('Failed to update item category status', 'error');
          }
        };

        return (
          <Tooltip title={isBlocked ? 'Blocked' : 'Unblocked'}>
            <IconButton size="small" onClick={toggleStatus}>
              {isBlocked ? (
                <BlockIcon color="error" fontSize="small" />
              ) : (
                <CheckCircleIcon color="success" fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    }] : []),

    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => <RowActions row={params.row} />,
    },
  ];

  const handleCategorySelection = (selectionModel: GridRowId[]) => {
    const ids = selectionModel.map((id) => String(id));
    setSelectedCategoryIds(ids);
  };

  return (
    <Box sx={categoryStyles.ListContainer}>
      {snackbarOpen && (
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ mb: 2 }}
        >
          {snackbarMessage}
        </Alert>
      )}

      <Grid
        container
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        wrap="wrap"
        sx={{ mb: 2 }}
      >
        <Box
          sx={{
            width: 600,
            border: '1px solid #ccc',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <TextField
            fullWidth
            size="medium"
            placeholder="Search by category name or store name"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '.MuiOutlinedInput-root': {
                height: 40,
                width: 600,
                border: 'none',
              },
              '& fieldset': {
                border: 'none',
              },
              '& .MuiInputBase-input': {
                paddingTop: '10px',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#aaa', mt: '4px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Grid
          container
          justifyContent="flex-end"
          spacing={1}
          alignItems="center"
        >
          <Grid>
            <PermissionActionButtons
              module="itemCategory"
              onBlock={showBlock ? handleBlock : undefined}
              onUnblock={showUnblock ? handleUnblock : undefined}
              onDelete={selectedCategoryIds.length ? handleDeleteClick : undefined}
            />
          </Grid>
          <Grid>
            {hasPermission('itemCategory', 'Add') && (
              <Button
                onClick={addCategory}
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
                startIcon={<Add sx={{ fontSize: 16 }} />}
              >
                Add Item Category
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Box width="100%" sx={{ overflowX: 'auto' }}>
        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={getItemCategory?.total ?? filteredRows.length}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={handleCategorySelection}
          selectionModel={selectedCategoryIds}
        />
      </Box>
    </Box>
  );
};

export default ItemCategoryList;